import { CircleAlertIcon, LoaderCircleIcon } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '@/components/core/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/core/card';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useMemberships } from '@/components/navigation-sidebar/account-menu/use-memberships';
import { useCurrentUserQuery } from '@/shared/hooks/queries/common';
import { useAuthToken } from '@/shared/hooks/useAuthToken';

// Only allow internal absolute paths. Reject protocol-relative ("//evil.com") and external URLs.
function sanitiseRedirectTarget(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/')) return '/';
  if (raw.startsWith('//')) return '/';
  return raw;
}

export function SwitchAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const accountId = searchParams.get('accountId') ?? undefined;
  const redirectTo = useMemo(
    () => sanitiseRedirectTarget(searchParams.get('redirectTo')),
    [searchParams],
  );

  const { accountId: currentAccountId, isLoading: isLoadingToken } = useAuthToken();
  const { data: { activeMembership, memberships } = {}, isLoading: isLoadingUser } =
    useCurrentUserQuery();
  const { selectMembership, changingToMembership } = useMemberships();

  // Pick the first membership whose accountId matches. SELLER/WAREHOUSE account IDs live in
  // distinct entity spaces, so a flat id match is unambiguous in practice.
  const targetMembership = useMemo(
    () => memberships?.find((m) => m.accountId === accountId),
    [memberships, accountId],
  );

  const hasDispatched = useRef(false);

  useEffect(() => {
    if (hasDispatched.current || !accountId || isLoadingToken || isLoadingUser || !memberships) {
      return;
    }

    // Already on the right account: just go. Auth0 and the current user must agree, otherwise
    // force a membership switch to resynchronise before redirecting.
    if (
      currentAccountId === accountId &&
      activeMembership?.accountId === accountId &&
      activeMembership.id === targetMembership?.id
    ) {
      hasDispatched.current = true;
      void navigate(redirectTo, { replace: true });
      return;
    }

    // No membership for this account: render the error state below.
    if (!targetMembership) return;

    hasDispatched.current = true;
    void selectMembership({
      membershipId: targetMembership.id,
      redirectTo,
      redirectOptions: { replace: true },
    });
  }, [
    accountId,
    activeMembership,
    currentAccountId,
    isLoadingToken,
    isLoadingUser,
    memberships,
    navigate,
    redirectTo,
    selectMembership,
    targetMembership,
  ]);

  // Error state: memberships loaded, but none matched the requested accountId.
  const noAccess = !isLoadingToken && !isLoadingUser && !!memberships && !targetMembership;

  if (noAccess) {
    return (
      <Page>
        <Header>
          <HeaderIcon>
            <CircleAlertIcon />
          </HeaderIcon>
          <HeaderTitle>No access</HeaderTitle>
        </Header>
        <PageContent variant="column" className="items-center justify-center">
          <div className="relative w-full max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle>You don&apos;t have access to this account</CardTitle>
                <CardDescription>
                  This link points to an account ({accountId}) that isn&apos;t in your membership
                  list. Ask the account owner for access, or check the link.
                </CardDescription>
              </CardHeader>
              <CardFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    void navigate('/');
                  }}
                >
                  Go home
                </Button>
              </CardFooter>
            </Card>
          </div>
        </PageContent>
      </Page>
    );
  }

  // Loading / switching state.
  const switchingTo = changingToMembership?.accountName ?? targetMembership?.accountName;
  return (
    <Page>
      <Header>
        <HeaderIcon>
          <LoaderCircleIcon className="animate-spin" />
        </HeaderIcon>
      </Header>
      <PageContent variant="column" className="items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <LoaderCircleIcon className="size-8 animate-spin" />
          <p>{switchingTo ? `Switching to ${switchingTo}...` : 'Loading...'}</p>
        </div>
      </PageContent>
    </Page>
  );
}
